<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCurrency } from 'dashboard/composables/useCurrency';
import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import MetricHelp from '../MetricHelp.vue';

const props = defineProps({
  data: {
    type: Object,
    default: null,
  },
});

const { locale } = useI18n();
const browserLocale = computed(() => locale.value.replace('_', '-'));
const { currency } = useCurrency();

// Estado
const sortColumn = ref('total_value');
const sortDirection = ref('desc');

// Computed
const agents = computed(() => {
  if (!props.data?.agents) return [];

  const agentsList = [...props.data.agents];

  // Ordenar
  agentsList.sort((a, b) => {
    const aValue = a[sortColumn.value];
    const bValue = b[sortColumn.value];

    if (
      sortColumn.value === 'average_time' ||
      sortColumn.value === 'time_to_assign'
    ) {
      // Para objetos com seconds
      const aSeconds = aValue?.seconds || 0;
      const bSeconds = bValue?.seconds || 0;
      return sortDirection.value === 'asc'
        ? aSeconds - bSeconds
        : bSeconds - aSeconds;
    }

    if (sortDirection.value === 'asc') {
      return aValue > bValue ? 1 : -1;
    }
    return aValue < bValue ? 1 : -1;
  });

  return agentsList;
});

// Fechados sem vendedor registrado: aparecem em linha própria, nunca no assignee
const unattributed = computed(() => props.data?.unattributed || {});
const hasUnattributed = computed(
  () =>
    (unattributed.value.won_count || 0) +
      (unattributed.value.lost_count || 0) +
      (unattributed.value.total_value || 0) >
    0
);
const unattributedWinRate = computed(() => {
  const won = unattributed.value.won_count || 0;
  const closed = won + (unattributed.value.lost_count || 0);
  return closed ? Math.round((won / closed) * 1000) / 10 : 0;
});

// Métodos
const formatCurrency = value => {
  return new Intl.NumberFormat(browserLocale.value, {
    style: 'currency',
    currency: currency.value,
  }).format(value);
};

const sortBy = column => {
  if (sortColumn.value === column) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortColumn.value = column;
    sortDirection.value = 'desc';
  }
};

const getSortIcon = column => {
  if (sortColumn.value !== column) return 'i-lucide-arrow-down-up';
  return sortDirection.value === 'asc'
    ? 'i-lucide-arrow-up'
    : 'i-lucide-arrow-down';
};
</script>

<template>
  <div
    class="bg-white dark:bg-n-slate-2 border border-n-slate-6 rounded-lg overflow-hidden"
  >
    <div class="px-4 py-3 border-b border-n-slate-6">
      <h3 class="text-lg font-semibold text-n-slate-12">
        {{ $t('KANBAN_REPORTS.TABLES.AGENTS_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11 mt-1">
        {{ $t('KANBAN_REPORTS.TABLES.AGENTS_DESCRIPTION') }}
      </p>
    </div>

    <div class="overflow-x-auto">
      <table class="w-full">
        <thead class="bg-n-slate-1 dark:bg-n-slate-3">
          <tr>
            <th
              class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider cursor-pointer hover:bg-n-slate-2 dark:hover:bg-n-slate-4 transition-colors"
              @click="sortBy('name')"
            >
              <div class="flex items-center gap-2">
                {{ $t('KANBAN_REPORTS.TABLES.AGENT') }}
                <Icon :icon="getSortIcon('name')" class="size-3.5" />
              </div>
            </th>
            <th
              class="px-4 py-3 text-right text-xs font-medium text-n-slate-11 uppercase tracking-wider cursor-pointer hover:bg-n-slate-2 dark:hover:bg-n-slate-4 transition-colors"
              @click="sortBy('total_cards')"
            >
              <div class="flex items-center justify-end gap-2">
                {{ $t('KANBAN_REPORTS.TABLES.CARDS_CREATED') }}
                <Icon :icon="getSortIcon('total_cards')" class="size-3.5" />
              </div>
            </th>
            <th
              class="px-4 py-3 text-right text-xs font-medium text-n-slate-11 uppercase tracking-wider cursor-pointer hover:bg-n-slate-2 dark:hover:bg-n-slate-4 transition-colors"
              @click="sortBy('total_value')"
            >
              <div class="flex items-center justify-end gap-2">
                {{ $t('KANBAN_REPORTS.TABLES.CARDS_VALUE') }}
                <Icon :icon="getSortIcon('total_value')" class="size-3.5" />
              </div>
            </th>
            <th
              class="px-4 py-3 text-right text-xs font-medium text-n-slate-11 uppercase tracking-wider cursor-pointer hover:bg-n-slate-2 dark:hover:bg-n-slate-4 transition-colors"
              @click="sortBy('won_count')"
            >
              <div class="flex items-center justify-end gap-2">
                {{ $t('CRM_METRICS.SALES.NAME') }}
                <MetricHelp metric-key="SALES" />
                <Icon :icon="getSortIcon('won_count')" class="size-3.5" />
              </div>
            </th>
            <th
              class="px-4 py-3 text-right text-xs font-medium text-n-slate-11 uppercase tracking-wider cursor-pointer hover:bg-n-slate-2 dark:hover:bg-n-slate-4 transition-colors"
              @click="sortBy('average_time')"
            >
              <div class="flex items-center justify-end gap-2">
                {{ $t('KANBAN_REPORTS.TABLES.TIME_IN_STAGE') }}
                <Icon :icon="getSortIcon('average_time')" class="size-3.5" />
              </div>
            </th>
            <th
              class="px-4 py-3 text-right text-xs font-medium text-n-slate-11 uppercase tracking-wider cursor-pointer hover:bg-n-slate-2 dark:hover:bg-n-slate-4 transition-colors"
              @click="sortBy('time_to_assign')"
            >
              <div class="flex items-center justify-end gap-2">
                {{ $t('KANBAN_REPORTS.TABLES.TIME_TO_ASSIGN') }}
                <Icon :icon="getSortIcon('time_to_assign')" class="size-3.5" />
              </div>
            </th>
            <th
              class="px-4 py-3 text-right text-xs font-medium text-n-slate-11 uppercase tracking-wider cursor-pointer hover:bg-n-slate-2 dark:hover:bg-n-slate-4 transition-colors"
              @click="sortBy('conversion_rate')"
            >
              <div class="flex items-center justify-end gap-2">
                {{ $t('CRM_METRICS.WIN_RATE.NAME') }}
                <MetricHelp metric-key="WIN_RATE" />
                <Icon :icon="getSortIcon('conversion_rate')" class="size-3.5" />
              </div>
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-slate-6">
          <tr
            v-for="agent in agents"
            :key="agent.id"
            class="hover:bg-n-slate-1 dark:hover:bg-n-slate-3 transition-colors"
          >
            <td class="px-4 py-3">
              <div class="flex items-center gap-3">
                <Avatar
                  :src="agent.avatar_url"
                  :name="agent.name"
                  :size="32"
                  rounded-full
                />
                <div>
                  <p class="text-sm font-medium text-n-slate-12">
                    {{ agent.name }}
                  </p>
                  <p class="text-xs text-n-slate-11">
                    {{ agent.email }}
                  </p>
                </div>
              </div>
            </td>
            <td class="px-4 py-3 text-right text-sm text-n-slate-12">
              {{ agent.total_cards }}
            </td>
            <td class="px-4 py-3 text-right text-sm font-medium text-green-600">
              {{ formatCurrency(agent.total_value) }}
            </td>
            <td class="px-4 py-3 text-right text-sm text-n-slate-12">
              {{ agent.won_count || 0 }}
            </td>
            <td class="px-4 py-3 text-right text-sm text-n-slate-12">
              {{ agent.average_time?.formatted || '0s' }}
            </td>
            <td class="px-4 py-3 text-right text-sm text-n-slate-12">
              {{ agent.time_to_assign?.formatted || '0s' }}
            </td>
            <td class="px-4 py-3 text-right text-sm font-medium text-blue-600">
              {{ agent.conversion_rate }}%
            </td>
          </tr>

          <tr v-if="hasUnattributed" data-test="unattributed-row">
            <td class="px-4 py-3">
              <p
                v-tooltip.top="
                  $t('CRM_METRICS.TEAM_PERFORMANCE.UNATTRIBUTED_HELP')
                "
                class="text-sm font-medium text-n-slate-12 cursor-help"
              >
                {{ $t('CRM_METRICS.TEAM_PERFORMANCE.UNATTRIBUTED') }}
              </p>
            </td>
            <td class="px-4 py-3 text-right text-sm text-n-slate-11">—</td>
            <td class="px-4 py-3 text-right text-sm text-n-slate-11">—</td>
            <td class="px-4 py-3 text-right text-sm text-n-slate-12">
              {{ unattributed.won_count || 0 }}
            </td>
            <td class="px-4 py-3 text-right text-sm text-n-slate-11">—</td>
            <td class="px-4 py-3 text-right text-sm text-n-slate-11">—</td>
            <td class="px-4 py-3 text-right text-sm font-medium text-blue-600">
              {{ unattributedWinRate }}%
            </td>
          </tr>

          <tr v-if="agents.length === 0 && !hasUnattributed">
            <td
              colspan="7"
              class="px-4 py-8 text-center text-sm text-n-slate-11"
            >
              {{ $t('KANBAN_REPORTS.TABLES.NO_DATA') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
